import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../lib/api';
import { prefetchEventWindow } from '../lib/eventQueries';
import type { Calendar } from 'commoncal-shared';

export type CalendarWithRole = Calendar & {
  role?: string | null;
  is_member?: boolean;
  is_subscribed?: boolean;
};

interface CalendarEvent {
  id: string;
  calendar_id: string;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  tags: string[];
  rrule: string | null;
  created_at: string;
  updated_at: string;
}

interface AllCalendarsState {
  calendars: Map<string, CalendarWithRole>;
  visibleCalendarIds: Set<string>;
  events: Map<string, CalendarEvent[]>;
  isLoadingCalendars: boolean;
  calendarsError: Error | null;
}

interface AllCalendarsContextValue extends AllCalendarsState {
  // Guest calendar (temporary access via join token)
  guestCalendar: CalendarWithRole | null;
  setGuestCalendar: (cal: CalendarWithRole | null) => void;

  // Calendar operations
  refreshCalendars: () => Promise<void>;
  toggleCalendarVisibility: (calendarId: string) => void;
  setCalendarVisibility: (
    calendarId: string,
    visible: boolean
  ) => void;
  setAllCalendarsVisible: (visible: boolean) => void;

  // Event operations
  fetchEventsForRange: (
    calendarId: string,
    from: Date,
    to: Date
  ) => Promise<void>;

  // Helpers
  getCalendar: (id: string) => CalendarWithRole | undefined;
  getCalendarColor: (id: string) => string;
  getVisibleCalendars: () => CalendarWithRole[];
  getVisibleEvents: () => CalendarEvent[];
  isCalendarVisible: (id: string) => boolean;
  /** True if the calendar appears in the sidebar (is_member OR is_subscribed) */
  isCalendarPinned: (id: string) => boolean;
  getPinnedCalendars: () => CalendarWithRole[];
}

const AllCalendarsContext =
  createContext<AllCalendarsContextValue | null>(null);

export function AllCalendarsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();

  const [guestCalendar, setGuestCalendar] =
    useState<CalendarWithRole | null>(null);

  // Fetch calendars via React Query
  const {
    data: calendarsData,
    isLoading: isLoadingCalendars,
    error: calendarsError,
  } = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.calendars.list(),
  });

  // Build calendars Map from query data
  const calendars = useMemo(() => {
    const map = new Map<string, CalendarWithRole>();
    if (calendarsData?.calendars) {
      calendarsData.calendars.forEach(
        (cal: CalendarWithRole) => {
          map.set(cal.id, cal);
        }
      );
    }
    return map;
  }, [calendarsData]);

  const [visibleCalendarIds, setVisibleCalendarIds] =
    useState<Set<string>>(new Set());
  const [events, setEvents] = useState<
    Map<string, CalendarEvent[]>
  >(new Map());

  // Auto-initialize visible calendars when data loads
  useEffect(() => {
    if (calendarsData?.calendars) {
      setVisibleCalendarIds(prev => {
        const newVisible = new Set(prev);
        calendarsData.calendars.forEach(
          (cal: CalendarWithRole) => {
            if (
              !newVisible.has(cal.id) &&
              !newVisible.has(`-${cal.id}`)
            ) {
              newVisible.add(cal.id);
            }
          }
        );
        return newVisible;
      });
    }
  }, [calendarsData]);

  useEffect(() => {
    const calendarIds = calendarsData?.calendars.map(
      (calendar: CalendarWithRole) => calendar.id
    );
    if (!calendarIds?.length) return;

    const prefetch = () => {
      if (!navigator.onLine) return;
      void prefetchEventWindow(
        queryClient,
        calendarIds
      ).catch(() => undefined);
    };

    prefetch();
    window.addEventListener('online', prefetch);
    return () =>
      window.removeEventListener('online', prefetch);
  }, [calendarsData, queryClient]);

  // Refresh calendars by invalidating the query
  const refreshCalendars = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['calendars'],
    });
  }, [queryClient]);
  const toggleCalendarVisibility = useCallback(
    (calendarId: string) => {
      setVisibleCalendarIds(prev => {
        const next = new Set(prev);
        if (next.has(calendarId)) {
          next.delete(calendarId);
        } else {
          next.add(calendarId);
        }
        return next;
      });
    },
    []
  );

  const setCalendarVisibility = useCallback(
    (calendarId: string, visible: boolean) => {
      setVisibleCalendarIds(prev => {
        const next = new Set(prev);
        if (visible) {
          next.add(calendarId);
        } else {
          next.delete(calendarId);
        }
        return next;
      });
    },
    []
  );

  const setAllCalendarsVisible = useCallback(
    (visible: boolean) => {
      if (visible) {
        setVisibleCalendarIds(new Set(calendars.keys()));
      } else {
        setVisibleCalendarIds(new Set());
      }
    },
    [calendars]
  );

  // Event fetching
  const fetchEventsForRange = useCallback(
    async (calendarId: string, from: Date, to: Date) => {
      try {
        const data = await api.events.list(calendarId, {
          from: from.toISOString(),
          to: to.toISOString(),
        });
        setEvents(prev => {
          const next = new Map(prev);
          next.set(calendarId, data.events);
          return next;
        });
      } catch (err) {
        console.error('Failed to fetch events:', err);
      }
    },
    []
  );

  // Helpers
  const getCalendar = useCallback(
    (id: string) => {
      return (
        calendars.get(id) ??
        (guestCalendar?.id === id
          ? guestCalendar
          : undefined)
      );
    },
    [calendars, guestCalendar]
  );

  const getCalendarColor = useCallback(
    (id: string) => {
      return (
        calendars.get(id)?.color ??
        (guestCalendar?.id === id
          ? guestCalendar.color
          : '#4a5568')
      );
    },
    [calendars, guestCalendar]
  );

  const getVisibleCalendars = useCallback(() => {
    return Array.from(calendars.values()).filter(cal =>
      visibleCalendarIds.has(cal.id)
    );
  }, [calendars, visibleCalendarIds]);

  const getVisibleEvents = useCallback(() => {
    const allEvents: CalendarEvent[] = [];
    for (const [calendarId, calendarEvents] of events) {
      if (visibleCalendarIds.has(calendarId)) {
        allEvents.push(...calendarEvents);
      }
    }
    return allEvents.sort(
      (a, b) =>
        new Date(a.start_at).getTime() -
        new Date(b.start_at).getTime()
    );
  }, [events, visibleCalendarIds]);

  const isCalendarVisible = useCallback(
    (id: string) => {
      return visibleCalendarIds.has(id);
    },
    [visibleCalendarIds]
  );

  const isCalendarPinned = useCallback(
    (id: string) => {
      const cal = calendars.get(id);
      if (!cal) return false;
      return Boolean(cal.is_member || cal.is_subscribed);
    },
    [calendars]
  );

  const getPinnedCalendars = useCallback(() => {
    return Array.from(calendars.values()).filter(
      cal => cal.is_member || cal.is_subscribed
    );
  }, [calendars]);

  const value = useMemo(
    () => ({
      calendars,
      visibleCalendarIds,
      events,
      isLoadingCalendars,
      calendarsError,
      guestCalendar,
      setGuestCalendar,
      refreshCalendars,
      toggleCalendarVisibility,
      setCalendarVisibility,
      setAllCalendarsVisible,
      fetchEventsForRange,
      getCalendar,
      getCalendarColor,
      getVisibleCalendars,
      getVisibleEvents,
      isCalendarVisible,
      isCalendarPinned,
      getPinnedCalendars,
    }),
    [
      calendars,
      visibleCalendarIds,
      events,
      isLoadingCalendars,
      calendarsError,
      guestCalendar,
      setGuestCalendar,
      refreshCalendars,
      toggleCalendarVisibility,
      setCalendarVisibility,
      setAllCalendarsVisible,
      fetchEventsForRange,
      getCalendar,
      getCalendarColor,
      getVisibleCalendars,
      getVisibleEvents,
      isCalendarVisible,
      isCalendarPinned,
      getPinnedCalendars,
    ]
  );

  return (
    <AllCalendarsContext.Provider value={value}>
      {children}
    </AllCalendarsContext.Provider>
  );
}

// Hook for accessing the full context
export function useAllCalendars(): AllCalendarsContextValue {
  const context = useContext(AllCalendarsContext);
  if (!context) {
    throw new Error(
      'useAllCalendars must be used within AllCalendarsProvider'
    );
  }
  return context;
}

// Hook for a single calendar
export function useCalendar(calendarId: string) {
  const {
    getCalendar,
    isCalendarVisible,
    toggleCalendarVisibility,
    getCalendarColor,
  } = useAllCalendars();

  const calendar = useMemo(
    () => getCalendar(calendarId),
    [getCalendar, calendarId]
  );
  const color = useMemo(
    () => getCalendarColor(calendarId),
    [getCalendarColor, calendarId]
  );
  const isVisible = useMemo(
    () => isCalendarVisible(calendarId),
    [isCalendarVisible, calendarId]
  );

  return {
    calendar,
    color,
    isVisible,
    toggleVisibility: useCallback(
      () => toggleCalendarVisibility(calendarId),
      [toggleCalendarVisibility, calendarId]
    ),
  };
}

// Hook for just the calendar color (lightweight)
export function useCalendarColor(
  calendarId: string
): string {
  const { getCalendarColor } = useAllCalendars();
  return useMemo(
    () => getCalendarColor(calendarId),
    [getCalendarColor, calendarId]
  );
}

// Hook for visible calendars list
export function useVisibleCalendars() {
  const { getVisibleCalendars } = useAllCalendars();
  return useMemo(
    () => getVisibleCalendars(),
    [getVisibleCalendars]
  );
}

// Hook for events from visible calendars
export function useVisibleEvents() {
  const { getVisibleEvents } = useAllCalendars();
  return useMemo(
    () => getVisibleEvents(),
    [getVisibleEvents]
  );
}
